import React, { useCallback, useMemo, useRef } from 'react';
import ReactQuill from 'react-quill-new';

import { useMediaUpload } from '../lib';

import { env } from '@/env';

import 'react-quill-new/dist/quill.snow.css';

/**
 * Heading sizes inside the editor, matched to the website's rich-text scale
 * (apps/website/lib/rich-text.ts: 30 / 24 / 20 / 18). Quill's own stylesheet
 * sizes headings in `em` off the editor's base, so an H2 looked bigger here
 * than it renders on the site; pinning the same px makes the editor an honest
 * preview. `!` beats quill.snow.css, which loads after Tailwind's utilities.
 */
/**
 * Keep the toolbar in reach on long documents (a "Read more" article runs well
 * past a screen, and formatting a heading near the bottom otherwise means
 * scrolling back to the top). It sticks inside the AppShell's <main> scroll
 * area. Needs an opaque background and a z-index, or the editor's own text
 * scrolls through it.
 *
 * z-20 stays UNDER the page's z-30 breadcrumb bar: if the two ever meet, the
 * bar wins rather than the toolbar covering the page title.
 *
 * Sticky is silently cancelled by an `overflow-hidden` ancestor. No call site
 * has one today; if a future one wraps the editor in a clipping card, the
 * toolbar will quietly go back to scrolling away.
 */
const STICKY_TOOLBAR = '[&_.ql-toolbar]:sticky [&_.ql-toolbar]:z-20 [&_.ql-toolbar]:bg-(--bg)';

/**
 * How far down the scrollport the toolbar parks — it has to clear whatever else
 * is already sticky above it, or it slides underneath (the bar is opaque and
 * sits at z-30, so the toolbar's top row simply disappears behind it).
 *
 * `page`: below the edit pages' sticky breadcrumb bar. The offsets come from
 * that bar's own geometry, same as the itinerary stepper rail:
 *   default: bar -top-6 (-24px) + h-12 (48px) => its bottom edge sits at 24px
 *   lg:      bar -top-8 (-32px) + h-12 (48px) => its bottom edge sits at 16px
 * `flush`: nothing sticky above — drawers, dialogs and plain tabs, where the
 * scroll container's own top is the ceiling.
 */
const STICKY_OFFSET = {
  page: '[&_.ql-toolbar]:top-6 lg:[&_.ql-toolbar]:top-4',
  flush: '[&_.ql-toolbar]:top-0',
} as const;

/**
 * Opening height of the writing area (it still grows with the content).
 *
 * `default` is the page-sized editor - a blog post, a policy document, the one
 * body on the screen. `compact` is for editors stacked inside a repeater, where
 * a dozen full-height ones would turn the form into a mile of scrollbar; it's
 * still tall enough to write a few formatted paragraphs against.
 */
const EDITOR_MIN_HEIGHT = {
  default: '[&_.ql-editor]:min-h-[300px]',
  compact: '[&_.ql-editor]:min-h-[170px]',
} as const;

const EDITOR_HEADINGS =
  '[&_.ql-editor_h1]:text-[30px]! [&_.ql-editor_h1]:leading-tight! [&_.ql-editor_h1]:font-bold! ' +
  '[&_.ql-editor_h2]:text-[24px]! [&_.ql-editor_h2]:leading-tight! [&_.ql-editor_h2]:font-bold! ' +
  '[&_.ql-editor_h3]:text-[20px]! [&_.ql-editor_h3]:leading-snug! [&_.ql-editor_h3]:font-semibold! ' +
  '[&_.ql-editor_h4]:text-[18px]! [&_.ql-editor_h4]:leading-snug! [&_.ql-editor_h4]:font-semibold! ' +
  '[&_.ql-editor_h5]:text-[1em]! [&_.ql-editor_h5]:font-semibold! ' +
  '[&_.ql-editor_h6]:text-[0.9em]! [&_.ql-editor_h6]:font-semibold! [&_.ql-editor_h6]:uppercase!';

/**
 * Stable, absolute delivery URL for a media id. Embedded as the `<img src>` in
 * saved content so the API re-resolves (re-signs) the underlying object on every
 * request - unlike a presigned URL, which would expire and break once frozen
 * into the stored HTML. Resolved to an absolute URL because website visitors
 * (a different origin than the admin) load these images.
 */
const mediaRawUrl = (id: string): string => {
  const base = env.VITE_API_BASE_URL;
  const origin = base.startsWith('/') ? `${window.location.origin}${base}` : base;
  return `${origin}/website/media/${id}/raw`;
};

interface RichTextEditorProps {
  /** Current HTML string (the persisted bodyHtml). */
  value: string;
  onChange: (html: string) => void;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  /** Upload folder for inline image uploads. */
  folder?: string;
  /**
   * Where the sticky toolbar parks. `page` (default) clears the edit pages'
   * sticky breadcrumb bar; pass `flush` inside a drawer, dialog or any scroll
   * container with nothing sticky above the editor.
   */
  stickyTop?: keyof typeof STICKY_OFFSET;
  /**
   * Opening height of the writing area. `compact` for editors repeated down a
   * form (one per card / item); `default` (the default) for a page's one body.
   */
  size?: keyof typeof EDITOR_MIN_HEIGHT;
}

/**
 * Rich-text editor backed by Quill (via `react-quill-new`, the React 19-safe
 * fork). Emits an HTML string stored as the blog post's `bodyHtml`.
 *
 * The toolbar image button is overridden to upload through the platform media
 * pipeline (presigned S3 flow) and embed the returned URL, rather than Quill's
 * default base64 inlining which bloats the stored HTML.
 */
export const RichTextEditor = ({
  value,
  onChange,
  label,
  placeholder,
  disabled,
  folder = 'blog',
  stickyTop = 'page',
  size = 'default',
}: RichTextEditorProps): React.JSX.Element => {
  const quillRef = useRef<ReactQuill>(null);
  const { upload } = useMediaUpload<{ id: string }>();

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const editor = quillRef.current?.getEditor();
      const range = editor?.getSelection(true);
      const index = range?.index ?? editor?.getLength() ?? 0;
      try {
        const asset = await upload(file, { kind: 'IMAGE', folder });
        editor?.insertEmbed(index, 'image', mediaRawUrl(asset.id), 'user');
        editor?.setSelection(index + 1, 0);
      } catch {
        /* surfaced by the upload hook */
      }
    };
    input.click();
  }, [upload, folder]);

  // Insert a starter 3-column table (header row + two body rows) at the cursor.
  // Uses the same clipboard path Quill uses for pasted tables, so it doesn't
  // depend on Quill 2's flaky native table module (which can break editor init).
  // The website renderer styles the <thead>/<th> as the branded navy header.
  const tableHandler = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    const cols = Math.min(
      8,
      Math.max(1, Math.floor(Number(window.prompt('Number of columns?', '3')))),
    );
    const rows = Math.min(
      50,
      Math.max(1, Math.floor(Number(window.prompt('Number of rows?', '3')))),
    );
    if (!cols || !rows) return;
    editor.getSelection(true);
    // Quill 2's registered table module owns the table blots, so this inserts a
    // real editable table (clipboard-pasting raw <table> HTML gets flattened
    // because Quill won't keep tags it has no blot for).
    const tableModule = editor.getModule('table') as
      | { insertTable?: (rows: number, cols: number) => void }
      | undefined;
    try {
      tableModule?.insertTable?.(rows, cols);
    } catch {
      /* table module unavailable in this build */
    }
  }, []);

  // Memoised so Quill is not re-initialised on every render.
  const modules = useMemo(
    () => ({
      // Registers Quill 2's table blots so tables survive (see tableHandler).
      table: true,
      toolbar: {
        container: [
          [{ header: [1, 2, 3, 4, 5, 6, false] }, { size: ['small', false, 'large', 'huge'] }],
          ['bold', 'italic', 'underline', 'strike'],
          [{ color: [] }, { background: [] }],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ indent: '-1' }, { indent: '+1' }],
          ['blockquote', 'code-block'],
          ['link', 'image'],
          ['table'],
          ['clean'],
        ],
        handlers: { image: imageHandler, table: tableHandler },
      },
    }),
    [imageHandler, tableHandler],
  );

  return (
    <div
      className={`uz-quill ${STICKY_TOOLBAR} ${STICKY_OFFSET[stickyTop]} ${EDITOR_HEADINGS} ${EDITOR_MIN_HEIGHT[size]} [&_.ql-container]:border-input [&_.ql-toolbar]:border-input [&_.ql-container]:rounded-b-lg [&_.ql-editor_table]:my-3 [&_.ql-editor_table]:w-full [&_.ql-editor_table]:border-collapse [&_.ql-editor_table:not(:has(th))_tr:first-child_td]:bg-[#093f51] [&_.ql-editor_table:not(:has(th))_tr:first-child_td]:font-semibold [&_.ql-editor_table:not(:has(th))_tr:first-child_td]:text-white [&_.ql-editor_td]:min-w-16 [&_.ql-editor_td]:border [&_.ql-editor_td]:border-(--border) [&_.ql-editor_td]:p-2 [&_.ql-editor_td]:align-top [&_.ql-table]:w-auto! [&_.ql-table]:px-2 [&_.ql-table]:text-[13px] [&_.ql-table]:font-medium [&_.ql-table]:after:content-['Table'] [&_.ql-toolbar]:rounded-t-lg`}
    >
      {label && (
        <label className="mb-1.5 block text-xs font-medium text-(--fg-muted)">{label}</label>
      )}
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        readOnly={disabled}
        placeholder={placeholder}
        modules={modules}
        onChange={(html, _delta, _source, editor) => {
          // Quill renders an empty doc as "<p><br></p>"; normalise to "".
          onChange(editor.getText().trim() ? html : '');
        }}
      />
    </div>
  );
};
